import { useEffect, useState, useCallback } from "react";
import { AlertTriangle, Boxes, Plus } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Badge, Empty, StatCard } from "@/components/ui-kit";

export default function Inventory() {
  const { user } = useAuth();
  const isAdmin = user.role === "ADMIN";
  const canMove = user.role === "ADMIN" || user.role === "PETUGAS_LAPANG";
  const [items, setItems] = useState([]);
  const [moves, setMoves] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [itemForm, setItemForm] = useState({ name: "", category: "Operasional", unit: "unit", min_stock: 0 });
  const [moveForm, setMoveForm] = useState({ item_id: "", movement_type: "IN", quantity: "", reason: "", reference: "" });

  const load = useCallback(() => {
    api.get("/inventory/items").then((r) => setItems(r.data.items)).catch(() => {});
    if (canMove) {
      api.get("/inventory/movements").then((r) => setMoves(r.data.items)).catch(() => {});
      api.get("/inventory/alerts").then((r) => setAlerts(r.data.items)).catch(() => {});
    }
  }, [canMove]);
  useEffect(() => { load(); }, [load]);

  const createItem = async () => {
    try { await api.post("/inventory/items", { ...itemForm, min_stock: Number(itemForm.min_stock) }); setItemForm({ name: "", category: "Operasional", unit: "unit", min_stock: 0 }); load(); }
    catch (e) { alert(e?.response?.data?.detail || "Gagal membuat barang"); }
  };

  const createMove = async () => {
    try { await api.post("/inventory/movements", { ...moveForm, quantity: Number(moveForm.quantity) }); setMoveForm({ item_id: "", movement_type: "IN", quantity: "", reason: "", reference: "" }); load(); }
    catch (e) { alert(e?.response?.data?.detail || "Gagal mencatat mutasi"); }
  };

  return (
    <div data-testid="inventory-page">
      <PageHeader kicker="Manajemen Inventori" title="Stok Koperasi" desc="Pantau BBM, es, alat tangkap, sembako, dan barang operasional koperasi." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--line)] border hairline mb-8">
        <Cell><StatCard label="Item Aktif" value={items.length} /></Cell>
        <Cell><StatCard label="Stok Minimum" value={alerts.length} index={1} danger={alerts.length > 0} /></Cell>
        <Cell><StatCard label="Mutasi Tercatat" value={moves.length} index={2} /></Cell>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        <section>
          <div className="flex items-center gap-2 mb-4"><Boxes size={18} /><h2 className="font-bold text-xl">Daftar Stok</h2></div>
          {isAdmin && (
            <div className="border hairline p-5 mb-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <input className="field tap px-4" placeholder="Nama barang" value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} />
              <input className="field tap px-4" placeholder="Kategori" value={itemForm.category} onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })} />
              <input className="field tap px-4" placeholder="Satuan" value={itemForm.unit} onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })} />
              <input className="field tap px-4" type="number" placeholder="Stok minimum" value={itemForm.min_stock} onChange={(e) => setItemForm({ ...itemForm, min_stock: e.target.value })} />
              <button onClick={createItem} disabled={!itemForm.name} className="tap btn-primary font-semibold md:col-span-2 disabled:opacity-40"><Plus size={16} className="inline mr-2" /> Tambah Item</button>
            </div>
          )}
          {items.length === 0 ? <Empty>Belum ada item inventori.</Empty> : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--line)] border hairline">
              {items.map((i) => {
                const low = i.stock <= i.min_stock;
                return (
                  <div key={i.item_id} className="bg-white p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold">{i.name}</p>
                        <p className="mono-label mt-1">{i.category}</p>
                      </div>
                      {low && <Badge tone="danger">Minimum</Badge>}
                    </div>
                    <p className="swiss-display text-3xl mt-5">{i.stock} <span className="text-base font-semibold">{i.unit}</span></p>
                    <p className="text-sm text-[var(--muted)] mt-2">Minimum {i.min_stock} {i.unit}</p>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {canMove && (
          <section>
            <div className="flex items-center gap-2 mb-4"><AlertTriangle size={18} /><h2 className="font-bold text-xl">Mutasi Stok</h2></div>
            <div className="border hairline p-5 mb-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <select className="field tap px-4 md:col-span-2" value={moveForm.item_id} onChange={(e) => setMoveForm({ ...moveForm, item_id: e.target.value })}>
                <option value="">Pilih barang</option>
                {items.map((i) => <option key={i.item_id} value={i.item_id}>{i.name} · stok {i.stock} {i.unit}</option>)}
              </select>
              <select className="field tap px-4" value={moveForm.movement_type} onChange={(e) => setMoveForm({ ...moveForm, movement_type: e.target.value })}>
                <option value="IN">Masuk</option><option value="OUT">Keluar</option>
              </select>
              <input className="field tap px-4" type="number" placeholder="Jumlah" value={moveForm.quantity} onChange={(e) => setMoveForm({ ...moveForm, quantity: e.target.value })} />
              <input className="field tap px-4 md:col-span-2" placeholder="Alasan mutasi" value={moveForm.reason} onChange={(e) => setMoveForm({ ...moveForm, reason: e.target.value })} />
              <button onClick={createMove} disabled={!moveForm.item_id || !moveForm.quantity || !moveForm.reason} className="tap btn-primary font-semibold md:col-span-2 disabled:opacity-40">Catat Mutasi</button>
            </div>
            {moves.length === 0 ? <Empty>Belum ada mutasi stok.</Empty> : (
              <div className="border hairline divide-y divide-[var(--line)]">
                {moves.slice(0, 12).map((m) => (
                  <div key={m.movement_id} className="p-4 flex items-center justify-between gap-4">
                    <div><p className="font-semibold">{m.item_name}</p><p className="text-sm text-[var(--muted)]">{m.reason} · oleh {m.recorded_by_name}</p></div>
                    <Badge tone={m.movement_type === "IN" ? "ok" : "outline"}>{m.movement_type === "IN" ? "+" : "-"}{m.quantity} {m.unit}</Badge>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

const Cell = ({ children }) => <div className="bg-white">{children}</div>;
